import React, { useMemo } from 'react';
import { FormConfig } from 'components/common/form/FormConfig';
import i18n from 'i18n';
import { FormContent } from 'components/common/form/Form';
import { useFormikContext } from 'formik';
import { ChannelGroupFormData } from 'core/channelGroup/ChannelGroup';
import {
  getCompositePixelSizeError,
  getCompositePixelSizeText
} from 'core/channelGroup/compositeSettingDisplay';
import styles from './channelGroupBasicForm.module.scss';

export const ChannelGroupOutdoorCreativeSizeForm: React.FC = () => {
  const { values } = useFormikContext<ChannelGroupFormData>();
  const outdoorCreativeSize =
    values.property.addonProps.outdoorCreativeSizes[0];
  const {
    isScreenSpliced,
    screenSplicedVerticalScreenCount: verticalScreenCount,
    screenSplicedHorizontalScreenCount: horizontalScreenCount
  } = values.screenProperty || {
    isScreenSpliced: false,
    screenSplicedVerticalScreenCount: 1,
    screenSplicedHorizontalScreenCount: 1
  };
  // ECOPD-5722: 合成設定另外取，不要併進上面那個 fallback 物件——
  // isCompositeCreative 是三態，補一個 false 進去就分不出「RMax 還沒同步」了。
  const compositeScreenProperty = values.screenProperty;
  const compositeSettingStatus =
    compositeScreenProperty?.compositeSettingStatus;
  const outdoorCreativeSizeFormConfig = useMemo(() => {
    const fieldsBuilder = new FormConfig.FieldsBuilder();
    if (outdoorCreativeSize) {
      if (isScreenSpliced) {
        fieldsBuilder.addLabel({
          label: i18n.t('channelGroup.labels.multiSplicedScreenCreativeSize'),
          value: `${outdoorCreativeSize.width * horizontalScreenCount} x ${outdoorCreativeSize.height * verticalScreenCount}`
        });
      }
      fieldsBuilder.addLabel({
        label: i18n.t('channelGroup.labels.singleScreenCreativeSize'),
        value: `${outdoorCreativeSize.width} x ${outdoorCreativeSize.height}`
      });
    }

    // ECOPD-5722: 實際播放尺寸跟其他兩個尺寸擺在一起。出現條件要跟後端輸出
    // compositeSettingStatus 的條件一樣，兩種都算：
    //   1. 連台渠道
    //   2. 連台已經關掉，但底下還留著連台素材類型
    // 少了第 2 種，那種渠道的素材會被擋下來，系統管理員卻在這一頁看不到任何異常。
    if (isScreenSpliced || compositeSettingStatus !== undefined) {
      const sizeError = getCompositePixelSizeError(compositeSettingStatus);
      fieldsBuilder.addLabel({
        label: i18n.t('channelGroup.labels.compositePixelSize'),
        value: getCompositePixelSizeText(compositeScreenProperty),
        hint: sizeError ? (
          <span className={styles.warning}>{sizeError}</span>
        ) : undefined
      });
    }

    return new FormConfig.Builder()
      .addSection(
        new FormConfig.SectionBuilder(fieldsBuilder.build())
          .withTitle(i18n.t('channelGroups.titles.outdoorCreativeSizesContent'))
          .build()
      )
      .build();
  }, [
    isScreenSpliced,
    outdoorCreativeSize,
    horizontalScreenCount,
    verticalScreenCount,
    compositeScreenProperty,
    compositeSettingStatus
  ]);

  return <FormContent formConfig={outdoorCreativeSizeFormConfig} />;
};
